import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { Document, Student } from "@markeroo/shared";
import type { UseMutationResult } from "@tanstack/react-query";
import PiLinkChainSlantStroke from "@markeroo/icons/src/stroke/development/PiLinkChainSlantStroke";
import PiEyeOnStroke from "@markeroo/icons/src/stroke/security/PiEyeOnStroke";
import { cardContainer, mutedText } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { DocumentCard, StudentSubmissionCard, type DocumentCardAction } from "../../../components/cards";
import { RegradeReasonTooltip } from "./RegradeReasonTooltip";
import { SearchInput } from "@/components/SearchInput";
import { Spinner } from "@/components/Spinner";
import { ui, placeholders } from "@/strings";
import { earlierPapers, papersForName, highlightNPapers } from "@/strings/labels";
import { useProgressStream } from "@/hooks/useProgressStream";

interface PapersSectionProps {
  assignmentId: string;
  items: Document[];
  isInProgress: boolean;
  overLimit: boolean;
  documentsLoading: boolean;
  documentsError: boolean;
  assessDocumentMutation: UseMutationResult<unknown, unknown, { assignmentId: string; documentId: string }>;
  deleteDocumentMutation: UseMutationResult<void, unknown, string>;
  deleteConfirmDoc: Document | null;
  queuingDocId: string | null;
  onOpenPasteModal: () => void;
  onAssessAllPapers: () => void;
  onEditDocument: (doc: Document) => void;
  onDeleteDocument: (doc: Document) => void;
  onViewMarkup: (docId: string) => void;
  onViewPaper: (docId: string) => void;
  onAssessDocument: (docId: string) => void;
  onRegradeDocument: (docId: string) => void;
  onOpenShareModal: (docId: string) => void;
  // Google Classroom mode - when true, papers are synced from GC and cannot be manually added
  isGCSyncedAssignment?: boolean;
  // Students in the class (for showing placeholder cards)
  students?: Student[];
  // Callback to upload paper for a specific student
  onUploadForStudent?: (studentId: string) => void;
}

/**
 * Determine if the regrade button should be shown for a document.
 * Show regrade if the document has an assessment AND has regrade reasons flagged.
 */
function shouldShowRegrade(doc: Document): boolean {
  return doc.hasAssessment && Boolean(doc.needsRegradeReason?.length);
}

/**
 * Wrapper component for DocumentCard that connects to the progress stream
 * when the document is being assessed.
 */
function DocumentCardWithProgress({
  assignmentId,
  documentId,
  isAssessing,
  ...cardProps
}: {
  assignmentId: string;
  documentId: string;
  isAssessing: boolean;
} & Omit<React.ComponentProps<typeof DocumentCard>, "streamingProgress">) {
  const progressStream = useProgressStream({
    assignmentId,
    documentId,
    enabled: isAssessing,
  });

  const streamingProgress = isAssessing
    ? {
        wordsGenerated: progressStream.wordsGenerated,
        stage: progressStream.stage,
        isConnected: progressStream.isConnected,
      }
    : null;

  return <DocumentCard {...cardProps} streamingProgress={streamingProgress} />;
}

export function PapersSection({
  assignmentId,
  items,
  isInProgress,
  overLimit,
  documentsLoading,
  documentsError,
  assessDocumentMutation,
  deleteDocumentMutation,
  deleteConfirmDoc,
  queuingDocId,
  onOpenPasteModal,
  onAssessAllPapers,
  onEditDocument,
  onDeleteDocument,
  onViewMarkup,
  onViewPaper,
  onAssessDocument,
  onRegradeDocument,
  onOpenShareModal,
  isGCSyncedAssignment = false,
  students = [],
  onUploadForStudent,
}: PapersSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const studentId = searchParams.get("studentId");
  const [searchQuery, setSearchQuery] = useState("");

  // Filter documents based on student filter and search query
  const filteredItems = useMemo(() => {
    let filtered = items;

    // Apply student filter first if present
    if (studentId) {
      filtered = filtered.filter((doc) => doc.studentId === studentId);
      // When viewing a specific student's papers, always show all of them
      // Sort by creation date, newest first
      return filtered.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    }

    // Apply search filter (only when not viewing a specific student)
    if (searchQuery.trim()) {
      filtered = filtered.filter((doc) =>
        doc.studentName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Always show latest per student (when not viewing a specific student)
    // Group by studentId, return newest per student
    const grouped = new Map<string, Document>();
    filtered.forEach((doc) => {
      const existing = grouped.get(doc.studentId);
      if (!existing || new Date(doc.createdAt) > new Date(existing.createdAt)) {
        grouped.set(doc.studentId, doc);
      }
    });
    return Array.from(grouped.values()).sort(
      (a, b) => a.studentName.localeCompare(b.studentName),
    );
  }, [items, studentId, searchQuery]);

  // Calculate related documents count for each document (papers created before it)
  // Also determine which documents should show share controls (newest per student in filtered view)
  const documentsWithRelated = useMemo(() => {
    // Group filtered documents by student to find the newest per student
    const newestPerStudent = new Map<string, string>();
    filteredItems.forEach((doc) => {
      const existing = newestPerStudent.get(doc.studentId);
      if (!existing) {
        newestPerStudent.set(doc.studentId, doc.id);
      } else {
        const existingDoc = filteredItems.find((d) => d.id === existing);
        if (existingDoc && new Date(doc.createdAt) > new Date(existingDoc.createdAt)) {
          newestPerStudent.set(doc.studentId, doc.id);
        }
      }
    });

    return filteredItems.map((doc) => {
      const studentPapers = items.filter((d) => d.studentId === doc.studentId);
      const earlierPapers = studentPapers.filter(
        (d) => d.id !== doc.id && new Date(d.createdAt) < new Date(doc.createdAt),
      );

      return {
        ...doc,
        relatedDocumentsCount: earlierPapers.length,
        showShareControls: doc.hasAssessment && newestPerStudent.get(doc.studentId) === doc.id,
      };
    });
  }, [filteredItems, items]);

  // Compute students who haven't submitted any papers yet
  // Only show when not viewing a specific student's papers
  // When searching, filter by student name to include matching students without submissions
  const studentsWithoutSubmissions = useMemo(() => {
    if (studentId) return [];
    const studentIdsWithSubmissions = new Set(items.map((doc) => doc.studentId));
    const query = searchQuery.trim().toLowerCase();
    return students
      .filter((s) => !s.archived && !studentIdsWithSubmissions.has(s.id))
      .filter((s) => !query || s.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, students, studentId, searchQuery]);

  const documentCount = items.length;
  const unassessedCount = items.filter((doc) => !doc.hasAssessment).length;
  const filteredStudentName = studentId
    ? items.find((doc) => doc.studentId === studentId)?.studentName
    : null;

  return (
    <section className={cardContainer}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg sm:text-xl font-semibold text-theme-primary">
              {studentId && filteredStudentName ? (
                <>
                  {papersForName(filteredStudentName)} {"("}
                  {filteredItems.length}
                  {")"}
                </>
              ) : (
                ui.headers.papers
              )}
            </h2>
            {studentId && filteredStudentName && (
              <button
                type="button"
                className={buttonVariants.link}
                onClick={() => setSearchParams({})}
              >
                {ui.labels.all} {ui.headers.papers}
              </button>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {!isGCSyncedAssignment && (
            <button
              type="button"
              data-tutorial-target="add-paper-button"
              className={`${items.every((doc) => doc.hasAssessment) ? buttonVariants.primary : buttonVariants.secondary} w-full sm:w-auto`}
              onClick={onOpenPasteModal}
            >
              {ui.actions.addPaper}
            </button>
          )}
          <button
            type="button"
            className={`${items.every((doc) => doc.hasAssessment) ? buttonVariants.secondary : buttonVariants.primary} ${isInProgress ? "flex items-center gap-2" : ""} w-full sm:w-auto`}
            onClick={onAssessAllPapers}
            disabled={overLimit || documentCount === 0 || items.every((doc) => doc.hasAssessment) || isInProgress}
          >
            {overLimit ? (
              ui.actions.upgrade
            ) : assessDocumentMutation.isPending ? (
              ui.actions.queuing
            ) : isInProgress ? (
              <>
                <Spinner size="sm" />
                {ui.loading.processing}
              </>
            ) : items.every((doc) => doc.hasAssessment) ? (
              ui.labels.allPapersHighlighted
            ) : (
              highlightNPapers(unassessedCount)
            )}
          </button>
          </div>
        </div>
      </div>
      {!studentId && (
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={placeholders.search.papers}
          aria-label="Filter papers by student name"
        />
      )}
      {documentsLoading ? <p className={mutedText}>{ui.loading.default}</p> : null}
      {documentsError ? <p className="text-sm text-semantic-error">{ui.labels.failedToLoadAssignments}</p> : null}
      {!documentCount && !(documentsLoading || documentsError) ? <p className={mutedText}>{ui.empty.noPapers}</p> : null}
      <ul className="mt-4 flex flex-col gap-4">
        {documentsWithRelated.map((doc) => {
          const hasAssessment = Boolean(doc.hasAssessment);
          const hasFailed = doc.lastPipelineStatus === "failed";
          // A document is "assessing" if:
          // 1. It's currently being queued (queuingDocId matches), OR
          // 2. Its pipeline status indicates it's in progress (pending/processing)
          // This applies to both new assessments AND re-highlighting existing assessments
          const isDocumentInPipeline = doc.lastPipelineStatus === "pending" || doc.lastPipelineStatus === "processing";
          const isAssessing = !hasFailed && (queuingDocId === doc.id || isDocumentInPipeline);
          // Prioritize "assessing" over "assessed" when re-highlighting is in progress
          const assessmentStatus = isAssessing
            ? "assessing"
            : hasAssessment
              ? "assessed"
              : hasFailed
                ? "assessmentFailed"
                : "needsAssessment";

          // Build actions array - all actions in one place, in priority order
          const actions: DocumentCardAction[] = [
            // Primary action: View markup (when assessed) or Assess/Retry paper (when needs assessment or failed)
            hasAssessment
              ? {
                  label: ui.actions.viewHighlights,
                  onClick: () => onViewMarkup(doc.id),
                  variant: "primary" as const,
                  dataTutorialTarget: "view-markup-button",
                }
              : !isAssessing || hasFailed
                ? {
                    label: queuingDocId === doc.id ? ui.actions.queuing : hasFailed ? ui.actions.highlightAgain : ui.actions.highlightPaper,
                    onClick: () => onAssessDocument(doc.id),
                    variant: "primary" as const,
                    loading: queuingDocId === doc.id,
                  }
                : null,
            // View Paper action (available for unassessed papers - allows viewing content before highlighting)
            !hasAssessment
              ? {
                  label: ui.actions.viewPaper,
                  onClick: () => onViewPaper(doc.id),
                  variant: "secondary" as const,
                  icon: <PiEyeOnStroke size={16} />,
                  disabled: isAssessing,
                }
              : null,
            // Share action (only for assessed documents that should show share controls)
            doc.showShareControls
              ? {
                  label: doc.shareToken ? ui.actions.manageShare : ui.actions.share,
                  onClick: () => onOpenShareModal(doc.id),
                  variant: "secondary" as const,
                  icon: <PiLinkChainSlantStroke size={16} />,
                }
              : null,
            // Edit action (only for text-based documents that aren't assessed yet and not GC-synced)
            doc.source === "text" && !hasAssessment && !doc.gcSubmissionId
              ? {
                  label: ui.actions.edit,
                  onClick: () => onEditDocument(doc),
                  variant: "link" as const,
                  disabled: isAssessing,
                }
              : null,
            // Regrade action (only shown when document has regrade reasons flagged)
            shouldShowRegrade(doc)
              ? {
                  label:
                    queuingDocId === doc.id
                      ? ui.actions.queuing
                      : ui.actions.highlightAgain,
                  onClick: () => onRegradeDocument(doc.id),
                  variant: "link" as const,
                  loading: queuingDocId === doc.id,
                  icon: <RegradeReasonTooltip reasons={doc.needsRegradeReason!} />,
                }
              : null,
            // Delete action (available unless GC-synced)
            !doc.gcSubmissionId
              ? {
                  label:
                    deleteDocumentMutation.isPending && deleteConfirmDoc?.id === doc.id
                      ? ui.actions.deleting
                      : ui.actions.delete,
                  onClick: () => onDeleteDocument(doc),
                  variant: "ghostDanger" as const,
                  loading: deleteDocumentMutation.isPending && deleteConfirmDoc?.id === doc.id,
                  disabled: isAssessing,
                }
              : null,
          ].filter((action): action is NonNullable<typeof action> => action !== null) as DocumentCardAction[];

          return (
            <li key={doc.id} data-testid="document-item">
              <DocumentCardWithProgress
                assignmentId={assignmentId}
                documentId={doc.id}
                isAssessing={isAssessing}
                studentName={doc.studentName}
                status={assessmentStatus}
                documentStatus={doc.status}
                issueCount={doc.issueCount}
                actions={actions}
                disabled={isAssessing}
                createdAt={doc.createdAt}
                emailBounced={doc.emailBounced}
                bounceReason={doc.bounceReason}
              >
                {doc.relatedDocumentsCount > 0 && (
                  <button
                    type="button"
                    className="text-sm text-primary hover:text-primary-hover"
                    onClick={() => {
                      if (studentId === doc.studentId) {
                        // Currently viewing this student's papers, toggle back to all papers
                        setSearchParams({});
                      } else {
                        // Not viewing this student yet, show their papers
                        setSearchParams({ studentId: doc.studentId });
                      }
                    }}
                  >
                    {studentId === doc.studentId ? ui.actions.hide : ui.actions.view} {doc.relatedDocumentsCount} {earlierPapers(doc.relatedDocumentsCount)}
                  </button>
                )}
              </DocumentCardWithProgress>
            </li>
          );
        })}
        {studentsWithoutSubmissions.map((student) => (
          <li key={student.id} data-testid="student-no-submission">
            <StudentSubmissionCard
              studentName={student.name}
              onUpload={
                !isGCSyncedAssignment && onUploadForStudent
                  ? () => onUploadForStudent(student.id)
                  : undefined
              }
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
